import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';
import { GsImage, HeroBackdropLayers } from '@/components/help-center/latest-power-shared';
import { BrandPeekSmilies } from '@/components/help-center/BrandPeekSmilies';
import AdminStarsLayer from '@/components/admin/AdminStarsLayer';
import AdminLoginFloatingSmilies from '@/components/admin/AdminLoginFloatingSmilies';
import {
  COMING_SOON_DEFAULTS,
  isComingSoonDescriptionHtml,
  type ComingSoonConfig,
} from '@/lib/coming-soon';
import * as comingSoonService from '@/services/coming-soon.service';
import { cn } from '@/lib/utils';

function clampProgress(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.min(100, Math.max(0, Math.round(value)));
}

/** Diamante do separador — com brilho que pulsa. */
function SeparatorCrystal() {
  return (
    <span
      className={cn(
        'coming-soon-separator-crystal relative inline-block h-2 w-2 shrink-0 rotate-45 rounded-[2px]',
        'bg-gradient-to-br from-cyan-100 via-sky-400 to-blue-600',
        'ring-1 ring-cyan-100/60'
      )}
      aria-hidden
    >
      <span className="absolute inset-[1.5px] rounded-[1px] bg-gradient-to-br from-white/90 via-cyan-200/35 to-transparent" />
    </span>
  );
}

/** Diamante nas badges — sem glow. */
function BadgeCrystal() {
  return (
    <span
      className={cn(
        'relative inline-block h-1.5 w-1.5 shrink-0 rotate-45 rounded-[1.5px]',
        'bg-gradient-to-br from-sky-200 via-sky-400 to-blue-600',
        'ring-1 ring-sky-300/40'
      )}
      aria-hidden
    >
      <span className="absolute inset-[1px] rounded-[0.5px] bg-gradient-to-br from-white/70 to-transparent" />
    </span>
  );
}

function BrandInline({ text }: { text: string }) {
  const parts = text.split(/(xat\s+Ajuda)/gi);
  return (
    <>
      {parts.map((part, index) =>
        /^xat\s+Ajuda$/i.test(part) ? (
          <span key={index} className="whitespace-nowrap">
            {part}
          </span>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </>
  );
}

function FeatureBadge({ label }: { label: string }) {
  return (
    <li
      className={cn(
        'flex min-h-[44px] items-center gap-2.5 rounded-xl px-3.5 py-2.5',
        'border border-white/[0.09] bg-white/[0.035]',
        'text-left font-inter text-[11.5px] font-medium leading-snug tracking-wide text-slate-100',
        'sm:min-h-[48px] sm:px-4 sm:text-xs',
        'transition-colors duration-200 hover:border-sky-400/25 hover:bg-white/[0.055]'
      )}
    >
      <BadgeCrystal />
      <span className="min-w-0 flex-1">
        <BrandInline text={label} />
      </span>
    </li>
  );
}

type ParsedDescription = {
  paragraphs: string[];
  features: string[];
};

function stripHtmlToText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function parseDescription(text: string): ParsedDescription {
  if (isComingSoonDescriptionHtml(text)) {
    if (typeof DOMParser === 'undefined') {
      return { paragraphs: [stripHtmlToText(text)], features: [] };
    }
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const paragraphs = Array.from(doc.querySelectorAll('p'))
      .map((p) => stripHtmlToText(p.innerHTML))
      .filter(Boolean);
    const features = Array.from(doc.querySelectorAll('li'))
      .map((li) => stripHtmlToText(li.innerHTML))
      .filter(Boolean);
    return { paragraphs, features };
  }

  const blocks = text.split('\n\n').map((b) => b.trim()).filter(Boolean);
  const paragraphs: string[] = [];
  const features: string[] = [];

  for (const block of blocks) {
    const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
    for (const line of lines) {
      if (line.startsWith('•') || line.startsWith('-')) {
        features.push(line.replace(/^[•-]\s*/, ''));
      } else {
        paragraphs.push(line);
      }
    }
  }

  return { paragraphs, features };
}

function ComingSoonDescription({ text }: { text: string }) {
  const { paragraphs, features } = useMemo(() => parseDescription(text), [text]);

  return (
    <div className="mx-auto mt-6 w-full text-center font-inter text-[12.5px] leading-snug text-blue-100/80 sm:mt-7 sm:text-[15px] sm:leading-relaxed">
      <div className="space-y-2 sm:space-y-2.5">
        {paragraphs.map((p) => (
          <p key={p} className="whitespace-nowrap font-normal">
            <BrandInline text={p} />
          </p>
        ))}
      </div>

      {features.length > 0 ? (
        <ul className="mx-auto mt-6 grid w-full max-w-[420px] list-none grid-cols-2 gap-2.5 p-0 sm:mt-7 sm:gap-3">
          {features.map((label) => (
            <FeatureBadge key={label} label={label} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function ComingSoonPublicPage() {
  const [config, setConfig] = useState<ComingSoonConfig>(COMING_SOON_DEFAULTS);
  const [progressReady, setProgressReady] = useState(false);

  useEffect(() => {
    void comingSoonService.getComingSoonConfig().then((remote) => {
      setConfig(remote ?? COMING_SOON_DEFAULTS);
    });
    const t = setTimeout(() => setProgressReady(true), 250);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlHeight = html.style.height;
    const prevBodyHeight = body.style.height;

    html.classList.add('coming-soon-page');
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.height = '100%';
    body.style.height = '100%';

    const blockScroll = (event: Event) => {
      event.preventDefault();
    };

    window.addEventListener('wheel', blockScroll, { passive: false });
    window.addEventListener('touchmove', blockScroll, { passive: false });

    return () => {
      html.classList.remove('coming-soon-page');
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      html.style.height = prevHtmlHeight;
      body.style.height = prevBodyHeight;
      window.removeEventListener('wheel', blockScroll);
      window.removeEventListener('touchmove', blockScroll);
    };
  }, []);

  const progress = clampProgress(config.progress);

  return (
    <div className="fixed inset-0 h-[100dvh] w-full overflow-hidden overscroll-none bg-[#030023] font-inter text-blue-50">
      <HeroBackdropLayers className="absolute inset-0" />
      <AdminStarsLayer />
      <div className="hidden sm:contents">
        <AdminLoginFloatingSmilies />
      </div>

      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#030023]/20 via-[#000b6a]/15 to-[#030023]/30"
        aria-hidden
      />

      <div className="relative z-10 flex h-full items-center justify-center overflow-hidden px-3 py-8 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 28, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className={cn(
            'relative w-full max-w-[640px] overflow-visible rounded-3xl border border-white/10',
            'bg-blue-950/40 px-4 py-8 shadow-[0_24px_80px_-24px_rgba(0,0,0,0.75),0_0_60px_-20px_rgba(56,189,248,0.18)]',
            'backdrop-blur-xl sm:px-11 sm:py-11'
          )}
        >
          <div className="pointer-events-none absolute left-1/2 top-0 z-20 -translate-x-[62%] -translate-y-[72%]">
            <BrandPeekSmilies />
          </div>

          <div className="mb-9 flex justify-center overflow-visible">
            <span className="whitespace-nowrap font-poppins text-4xl leading-none tracking-tight text-white sm:text-5xl">
              <span className="font-semibold">xat</span>{' '}
              <span className="font-bold">Ajuda</span>
            </span>
          </div>

          <div className="mx-auto mb-7 flex max-w-[220px] items-center gap-3">
            <span className="h-px flex-1 bg-gradient-to-r from-transparent to-sky-400/50" />
            <SeparatorCrystal />
            <span className="h-px flex-1 bg-gradient-to-l from-transparent to-sky-400/50" />
          </div>

          <div className="text-center">
            <h1 className="bg-gradient-to-r from-sky-300 via-cyan-200 to-sky-400 bg-clip-text font-poppins text-3xl font-bold tracking-tight text-transparent sm:text-4xl">
              {config.title}
            </h1>
            <ComingSoonDescription text={config.description} />

            {config.estimatedLaunch.trim() ? (
              <div className="mx-auto mt-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-4 py-2">
                <Clock className="h-4 w-4 text-sky-300/80" aria-hidden />
                <span className="font-inter text-xs font-medium text-slate-300">
                  {config.estimatedLaunch}
                </span>
              </div>
            ) : null}
          </div>

          <div className="coming-soon-float mt-10 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm">
            <div className="mb-3 flex items-center justify-between gap-3">
              <span className="inline-flex min-w-0 items-center gap-2">
                <GsImage
                  code="coding"
                  className="flex h-[4.75rem] w-[4.75rem] shrink-0 items-center justify-center"
                  imgClassName="max-h-[76px] max-w-[76px] object-contain"
                />
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="font-poppins text-sm font-semibold text-white">
                    Construção da nova plataforma
                  </span>
                  <span className="font-inter text-[11px] text-slate-400">
                    {config.progressStatus}
                  </span>
                </span>
              </span>
              <span className="shrink-0 font-poppins text-lg font-bold text-sky-200 tabular-nums">
                {progress}%
              </span>
            </div>

            <div
              className="relative h-3.5 w-full overflow-hidden rounded-full border border-white/10 bg-white/[0.04]"
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Progresso da nova plataforma"
            >
              <div
                className="coming-soon-bar-fill relative h-full rounded-full bg-gradient-to-r from-sky-600 via-sky-400 to-cyan-300 transition-[width] duration-1000 ease-out"
                style={{ width: progressReady ? `${progress}%` : '0%' }}
              >
                <div className="absolute inset-0 overflow-hidden rounded-full">
                  <div className="coming-soon-shimmer absolute inset-y-0 -left-1/3 w-1/3 skew-x-[-20deg] bg-gradient-to-r from-transparent via-white/70 to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
